import { Controller } from '../utils/controller';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ymkAsyncInit: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YMK: any;
  }
}

interface IShade {
  sku: string;
  hex: string;
}

interface IFoundationShade {
  hex?: string;
  cooler?: string;
  lighter?: string;
  guid?: string;
  darker?: string;
  warmer?: string;
}

const constants = {
  makeup: 'makeup',
  foundation: 'foundation',
  skincare: 'skincare',
  moduleId: 'YMK-module'
};

const shades: IShade[] = [
  { sku: 'PKL412', hex: '#b3231a' },
  { sku: 'PKL410', hex: '#b52024' },
  { sku: 'PKL411', hex: '#bc444e' },
  { sku: 'PKL406', hex: '#c72c2a' },
  { sku: 'PKL402', hex: '#c8634f' },
  { sku: 'PKL421', hex: '#c90101' },
  { sku: 'PKL404', hex: '#d00024' },
  { sku: 'PKL405', hex: '#d5080d' },
  { sku: 'PKL403', hex: '#df4f46' },
  { sku: 'PKL409', hex: '#e21033' },
  { sku: 'PKL418', hex: '#b13100' },
  { sku: 'PKL417', hex: '#a22600' },
  { sku: 'PKL413', hex: '#a14947' },
  { sku: 'PKL407', hex: '#9d1d1e' },
  { sku: 'PKL401', hex: '#895446' },
  { sku: 'PKL414', hex: '#88252a' },
  { sku: 'PKL416', hex: '#864c40' },
  { sku: 'PKL420', hex: '#851c16' },
  { sku: 'PKL415', hex: '#570033' }
];

const foundationShades: IFoundationShade[] = [
  { hex: '#e5ad8a' },
  { hex: '#f2bf91' },
  { hex: '#c48f56' },
  { hex: '#c27440' },
  { hex: '#d69060' }
];

export default class extends Controller {
  static targets = ['iframeParent', 'loader', 'launchButton'];
  static requiredTargets = [];

  static values = {
    autoOpen: Boolean,
    foundationShades: Object,
    language: {
      type: String,
      default: 'en'
    },
    isMobile: Boolean,
    isYMKModuleLoaded: Boolean,
    isYMKModuleOpen: Boolean,
    shades: {
      type: Array,
      default: shades
    },
    selectedsku: String,
    apikey: String,
    id: String,
    type: String,
    skincareResults: Array,
    productIdList: {
      type: Array,
      default: ['22830']
    },
    width: Number,
    height: Number
  };

  // TARGETS
  declare iframeParentTarget: HTMLDivElement;
  declare launchButtonTarget: HTMLButtonElement;
  declare loaderTarget: HTMLElement;

  // VALUES
  private apikeyValue: string;
  private autoOpenValue: boolean;
  private foundationShadesValue: IFoundationShade;
  private heightValue: number;
  private idValue: string;
  private isMobileValue: boolean;
  private isYMKModuleLoadedValue: boolean;
  private languageValue: string;
  private productIdListValue: string[];
  private selectedskuValue: string;
  private shadesValue: IShade[];
  private typeValue: string;
  private widthValue: string;

  initialize() {
    if (this.autoOpenValue) {
      this.launchVTO();
    }
  }

  launchVTO() {
    this.loaderTarget.classList.remove('hidden');
    this.iframeParentTarget.classList.remove('hidden');
    this.launchButtonTarget.disabled = true;

    this.isMobileValue = screen.width <= 414;
    const width = this.isMobileValue
      ? screen.width
      : this.widthValue || this.iframeParentTarget.offsetWidth;
    const height = this.isMobileValue
      ? screen.width * 1.2
      : this.heightValue || this.iframeParentTarget.offsetHeight;
    this.createYMKElement();

    if (!this.isYMKModuleLoadedValue) {
      this.loadYMK(document, this.apikeyValue, this.idValue, this.typeValue);
    } else {
      this.openVTOByType(this.typeValue);
    }

    window.ymkAsyncInit = () => {
      window.YMK.init({
        autoOpen: this.typeValue !== constants.skincare,
        showCloseBtnOnLauncher: true,
        language: this.languageValue,
        ...(this.typeValue === constants.makeup && { height }),
        ...(this.typeValue === constants.makeup && { width }),
        ...(this.typeValue === constants.skincare &&
          this.isMobileValue && { height: screen.height }),
        ...(this.typeValue === constants.skincare && this.isMobileValue && { width: screen.width }),
        ...(this.typeValue === constants.foundation && {
          productIdList: this.productIdListValue
        })
      });

      if (this.typeValue === constants.foundation || this.typeValue === constants.skincare) {
        this.openVTOByType(this.typeValue);
      }

      this.addYMKEventListeners();
    };
  }

  /**
   * https://www.perfectcorp.com/document-center/en/#youcam-for-web-get-started
   * @param {Object} d window document
   * @param {String} k vto api key
   * @param {String} id vto id
   * @param {String} type vto type
   */
  loadYMK(d, k, id, type) {
    const s = d.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    const sub = this.getURLSubdirectoryByType(type);
    s.src = `https://plugins-media.perfectcorp.com/${id}${sub}/sdk.js?apiKey=` + k;
    const x = d.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);
  }

  getURLSubdirectoryByType(type) {
    switch (type) {
      case constants.makeup:
        return '';
      case constants.foundation:
        return '-shadefinder';
      case constants.skincare:
        return '-skincare';
      default:
        return '';
    }
  }

  openVTOByType(type) {
    switch (type) {
      case constants.makeup:
        return window.YMK.open();
      case constants.foundation:
        return window.YMK.open(true, null, true);
      case constants.skincare:
        return window.YMK.openSkincare('capture');
      default:
        return window.YMK.open();
    }
  }

  createYMKElement() {
    const ymkElement = document.getElementById('YMK-module');
    if (ymkElement != null) {
      return;
    }

    const YMKElement = document.createElement('div');
    YMKElement.id = constants.moduleId;

    this.isMobileValue
      ? document.body.insertBefore(YMKElement, document.body.firstChild)
      : this.iframeParentTarget.appendChild(YMKElement);
  }

  addYMKEventListeners() {
    window.YMK.addEventListener('loading', () => {
      if (this.isMobileValue) {
        const YMKElement = document.getElementById(constants.moduleId);
        YMKElement?.classList.add('h-screen', 'bg-white');
      }
    });

    window.YMK.addEventListener('loaded', () => {
      this.isYMKModuleLoadedValue = true;
      if (this.typeValue === constants.makeup) {
        window.YMK.applyMakeupBySku(this.selectedskuValue);

        if (this.isMobileValue) {
          const YMKElement = document.getElementById(constants.moduleId);
          const overlay = this.generateVTOShadesOverlay();

          YMKElement?.appendChild(overlay);
        }
      }
    });

    window.YMK.addEventListener('opened', () => {
      this.loaderTarget.classList.add('hidden');
      this.launchButtonTarget.classList.add('hidden');

      if (this.isMobileValue) {
        window.scrollTo(0, 0);
        document.body.classList.add('overflow-hidden');
      }
    });

    window.YMK.addEventListener('skinShadeFound', (result) => {
      this.foundationShadesValue = result.readableRecommendations[0];
      window.YMK.applyMakeupBySku(result.readableRecommendations[0].guid);
      this.generateFoundationShadeOverlay();
    });

    window.YMK.addEventListener('skinAnalysisFinished', (/*result*/) => {
      document.body.classList.remove('overflow-hidden');
    });

    window.YMK.addEventListener('closed', () => {
      document.body.classList.remove('overflow-hidden');
      this.loaderTarget.classList.add('hidden');
      this.iframeParentTarget.classList.add('hidden');
      this.launchButtonTarget.classList.remove('hidden');
      this.launchButtonTarget.disabled = false;
      const ymkElement = document.getElementById('YMK-module');
      ymkElement?.remove();
      window.YMK.reset();
    });
  }

  generateShadeButton(hex: string, sku) {
    const button = document.createElement('button');
    button.classList.add('rounded-full', 'w-8', 'h-8', 'mr-4');
    button.style.background = hex;
    button.onclick = () => window.YMK.applyMakeupBySku(sku);

    return button;
  }

  generateShadeWrapper() {
    const shadeContainer = document.createElement('div');
    shadeContainer.classList.add('overflow-scroll', 'whitespace-nowrap', 'h-12');

    for (const shade of this.shadesValue) {
      const shadeButton = this.generateShadeButton(shade.hex, shade.sku);
      shadeContainer.appendChild(shadeButton);
    }

    return shadeContainer;
  }

  generateVTOShadesOverlay() {
    const YMKiFrame = document.getElementById('YMK-module-iframe');

    const wrapper = document.createElement('div');
    wrapper.classList.add('grid', 'p-4', 'content-between');
    wrapper.style.height = `${screen.height - (YMKiFrame ? YMKiFrame.offsetHeight : 0)}px`;

    const shadeContainer = this.generateShadeWrapper();

    const addToBagTemplate = document.getElementById('vto-add-to-bag-button');
    const addToBagTemplateContent = (addToBagTemplate as HTMLTemplateElement).content;
    const clonedContent = addToBagTemplateContent.cloneNode(true);

    wrapper.append(shadeContainer);
    wrapper.appendChild(clonedContent);

    return wrapper;
  }

  generateFoundationShadeButton(hex, sku) {
    const button = document.createElement('button');
    button.classList.add('rounded-full', 'w-10', 'h-10');
    button.style.background = hex;
    button.onclick = () => window.YMK.applyMakeupBySku(sku);

    return button;
  }

  generateFoundationShadeOverlay() {
    const shadeContainer = document.createElement('div');
    shadeContainer.classList.add('flex', 'gap-4', 'justify-between');

    const cooler = this.generateFoundationShadeButton(
      foundationShades[0].hex,
      this.foundationShadesValue.cooler
    );
    const lighter = this.generateFoundationShadeButton(
      foundationShades[1].hex,
      this.foundationShadesValue.lighter
    );
    const best = this.generateFoundationShadeButton(
      foundationShades[2].hex,
      this.foundationShadesValue.guid
    );
    const darker = this.generateFoundationShadeButton(
      foundationShades[3].hex,
      this.foundationShadesValue.darker
    );
    const warmer = this.generateFoundationShadeButton(
      foundationShades[4].hex,
      this.foundationShadesValue.warmer
    );

    shadeContainer.appendChild(cooler);
    shadeContainer.appendChild(lighter);
    shadeContainer.appendChild(best);
    shadeContainer.appendChild(darker);
    shadeContainer.appendChild(warmer);

    const YMKElement = document.getElementById('YMK-module');
    const YMKiFrame = document.getElementById('YMK-module-iframe');

    const wrapper = document.createElement('div');
    wrapper.classList.add(
      'grid',
      'p-4',
      'bg-white',
      'absolute',
      'left-0',
      'right-0',
      'bottom-0',
      'z-10',
      'h-52',
      'content-between'
    );

    wrapper.appendChild(shadeContainer);

    const addToBagTemplate = document.getElementById('vto-add-to-bag-button');
    const addToBagTemplateContent = (addToBagTemplate as HTMLTemplateElement).content;
    const clonedContent = addToBagTemplateContent.cloneNode(true);

    wrapper.append(shadeContainer);
    wrapper.appendChild(clonedContent);

    if (YMKiFrame) {
      YMKiFrame.style.height = `${screen.height - 208}px`;
    }
    YMKElement?.appendChild(wrapper);
  }
}
